import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROUTES } from '@/constants';
import { settingsService } from '@/services/api/settings.service';
import { clearTokens } from '@/services/api/tokens';
import { useAppDispatch } from '@/store/hooks';
import { sessionEnded } from '@/store/slices';
import type { UpdateSettingsRequest } from '@/types';
import { queryKeys } from './keys';

/** Staff-wide security policy and notification toggles. */
export function useSystemSettings() {
  return useQuery({
    queryKey: queryKeys.settings.all,
    queryFn: ({ signal }) => settingsService.get(signal),
  });
}

export function useUpdateSystemSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateSettingsRequest) => settingsService.update(body),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.settings.all, settings);
    },
  });
}

/**
 * Signs every staff account out of every device. That includes the tab that
 * clicked the button, so success ends this session locally too, the same way
 * `useLogout` does, rather than leaving a signed-out account behind a page
 * that still looks live until its next request 401s.
 */
export function useRevokeAllSessions() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsService.revokeAllSessions(),
    onSuccess: async () => {
      toast.success(`Signed out all admin sessions, including yours.`);
      clearTokens();
      dispatch(sessionEnded());
      queryClient.clear();
      await navigate(ROUTES.login, { replace: true });
    },
  });
}
